// Admin Retailers — list + drill-down for every retailer on the platform.
//
// Spec: TKOS_MASTER_CONTEXT.md §ADMIN PORTAL + inventory_sections/02_admin_supplier.md.
// Source-of-truth render lives in admin-standalone.html ~1302-1340 (page-retailers
// shell) and ~3107-3220 (renderRetailerList / renderRetailerDetail).
//
// Data sources
// ------------
// Live endpoints (verified 2026-05 against /var/www/kitchenos-api/server.js):
//   GET    /api/admin/retailers              — REAL. server.js:892.
//   DELETE /api/admin/retailers/:id          — REAL but destructive. We don't use
//                                                it from this page; suspend/reactivate
//                                                are status mutations instead.
//   GET    /api/stripe/admin/overview        — REAL. Used to enrich rolling turnover
//                                                + commission rate per retailer.
//
// Stubbed (no backend yet):
//   GET    /api/admin/retailers/:id          — falls back to the list-row payload.
//   PATCH  /api/admin/retailers/:id          — suspend/reactivate flow stubs the
//                                                call when 404/501 comes back so
//                                                the UX still works in demo.
//   /api/admin/audit/:retailerId             — audit log viewer (inventory: ABSENT).
//                                                We render the panel with an empty
//                                                state.
//   /api/admin/retailers/:id/users           — team members (no endpoint yet).
//
// Tier model — mirrors kitchenos-api/modules/stripe.js TIERS.
type TierDef = { n: 1 | 2 | 3 | 4 | 5; minGbp: number; maxGbp: number | null; rate: number; label: string };
const TIERS: readonly TierDef[] = [
  { n: 1, minGbp: 0,         maxGbp: 250_000,    rate: 3.3, label: "Tier 1" },
  { n: 2, minGbp: 250_000,   maxGbp: 500_000,    rate: 2.8, label: "Tier 2" },
  { n: 3, minGbp: 500_000,   maxGbp: 750_000,    rate: 2.3, label: "Tier 3" },
  { n: 4, minGbp: 750_000,   maxGbp: 1_000_000,  rate: 1.8, label: "Tier 4" },
  { n: 5, minGbp: 1_000_000, maxGbp: null,       rate: 1.5, label: "Tier 5" },
];

// Enterprise ladder — mirror of kitchenos-api/modules/stripe.js ENTERPRISE_TIERS.
// Manually assigned per retailer (admin-only); same £500 cap + rolling-12-month
// logic, 1.5% floor (top band open-ended above £1m).
const ENTERPRISE_TIERS: readonly TierDef[] = [
  { n: 1, minGbp: 0,         maxGbp: 250_000,    rate: 2.5,  label: "Tier 1" },
  { n: 2, minGbp: 250_000,   maxGbp: 500_000,    rate: 2.25, label: "Tier 2" },
  { n: 3, minGbp: 500_000,   maxGbp: 750_000,    rate: 2.0,  label: "Tier 3" },
  { n: 4, minGbp: 750_000,   maxGbp: 1_000_000,  rate: 1.75, label: "Tier 4" },
  { n: 5, minGbp: 1_000_000, maxGbp: null,       rate: 1.5,  label: "Tier 5" },
];

type Plan = "standard" | "enterprise";

function tiersForPlan(plan: Plan): readonly TierDef[] {
  return plan === "enterprise" ? ENTERPRISE_TIERS : TIERS;
}

import { ApiError } from "@tkos/api-client";
import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../types";
import { formatAddress } from "../../types";

import "./styles.css";

// ── Server shapes ────────────────────────────────────────────────────────────

interface ServerRetailer {
  id: string;
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  status: string;
  createdAt: string;
  settings?: {
    stripeConnected?: boolean;
    lastLoginAt?: string;
    address?: unknown;
    slug?: string;
  };
  /** Legacy field — older retailer records nest these under `profile`. */
  profile?: {
    address?: unknown;
    slug?: string;
  };
}

interface ByRetailerRow {
  retailerId: string;
  commissionPence: number;
  gmvPence: number;
  txCount: number;
}

interface AdminOverview {
  byRetailer: ByRetailerRow[];
  /** retailerId → commission plan. Absent entries are 'standard'. */
  plans?: Record<string, Plan>;
}

// ── View model ───────────────────────────────────────────────────────────────

type RetailerStatus = "active" | "suspended" | "cancelled" | "onboarding" | "pending" | "unknown";

interface RetailerRow {
  id: string;
  businessName: string;
  slug: string;
  email: string;
  phone: string;
  contactName: string;
  status: RetailerStatus;
  stripeConnected: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  rollingGmvPence: number;
  plan: Plan;
  tierN: 1 | 2 | 3 | 4 | 5;
  commissionRatePct: number;
  address: unknown;
}

type StatusFilter = "all" | "active" | "suspended" | "cancelled";
type TierFilter = "all" | 1 | 2 | 3 | 4 | 5;

// ── Formatters ───────────────────────────────────────────────────────────────

const GBP_BIG = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

const RTF = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });
const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

function gbpFromPence(pence: number): string {
  return GBP_BIG.format(Math.round(pence / 100));
}

function relTime(iso: string | null): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const diffSec = Math.round((t - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return RTF.format(diffSec, "second");
  if (abs < 3600) return RTF.format(Math.round(diffSec / 60), "minute");
  if (abs < 86400) return RTF.format(Math.round(diffSec / 3600), "hour");
  if (abs < 86400 * 30) return RTF.format(Math.round(diffSec / 86400), "day");
  if (abs < 86400 * 365) return RTF.format(Math.round(diffSec / (86400 * 30)), "month");
  return RTF.format(Math.round(diffSec / (86400 * 365)), "year");
}

function fmtDateShort(iso: string): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return DATE_FMT.format(new Date(t));
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
}

function normaliseStatus(s: string): RetailerStatus {
  const lc = (s || "").toLowerCase();
  if (lc === "active" || lc === "suspended" || lc === "cancelled") return lc;
  if (lc === "onboarding" || lc === "pending") return lc;
  return "unknown";
}

// ── Tier helpers ─────────────────────────────────────────────────────────────

function tierForGbp(
  gbp: number,
  tiers: readonly TierDef[] = TIERS,
): { n: 1 | 2 | 3 | 4 | 5; rate: number; label: string } {
  for (const t of tiers) {
    if (gbp >= t.minGbp && (t.maxGbp === null || gbp < t.maxGbp)) {
      return { n: t.n, rate: t.rate, label: t.label };
    }
  }
  const last = tiers[tiers.length - 1]!;
  return { n: last.n, rate: last.rate, label: last.label };
}

// ── Backend fetch ────────────────────────────────────────────────────────────

async function loadRetailers(
  ctx: PageContext,
  signal: AbortSignal,
): Promise<{ rows: RetailerRow[]; overviewMissing: boolean }> {
  const [retailersRes, overviewRes] = await Promise.allSettled([
    ctx.api<ServerRetailer[]>("/admin/retailers", { signal }),
    ctx.api<AdminOverview>("/stripe/admin/overview", { signal }),
  ]);

  if (signal.aborted) throw new DOMException("aborted", "AbortError");

  if (retailersRes.status === "rejected") {
    throw retailersRes.reason;
  }

  const rawRetailers = retailersRes.value;
  const overview =
    overviewRes.status === "fulfilled" ? overviewRes.value : null;
  const overviewMissing = overview === null;

  // Build retailerId → byRetailer row for fast lookup.
  const byId = new Map<string, ByRetailerRow>();
  for (const r of overview?.byRetailer ?? []) {
    byId.set(r.retailerId, r);
  }

  const rows: RetailerRow[] = rawRetailers.map((r) => {
    const stats = byId.get(r.id);
    const gmvPence = stats?.gmvPence ?? 0;
    const plan: Plan = overview?.plans?.[r.id] === "enterprise" ? "enterprise" : "standard";
    const tier = tierForGbp(gmvPence / 100, tiersForPlan(plan));
    const slug = r.settings?.slug || r.profile?.slug || slugify(r.businessName || r.id);
    const address = r.settings?.address ?? r.profile?.address ?? null;
    return {
      id: r.id,
      businessName: r.businessName || "(unnamed)",
      slug,
      email: r.email || "",
      phone: r.phone || "",
      contactName: r.contactName || "",
      status: normaliseStatus(r.status),
      stripeConnected: r.settings?.stripeConnected === true,
      lastLoginAt: r.settings?.lastLoginAt ?? null,
      createdAt: r.createdAt || "",
      rollingGmvPence: gmvPence,
      plan,
      tierN: tier.n,
      commissionRatePct: tier.rate,
      address,
    };
  });

  return { rows, overviewMissing };
}

interface RetailerDetail {
  retailer: RetailerRow;
  /** Real transactions if we can find them, otherwise []. */
  recentTx: { id: string; createdAt: number; grossPence: number; commissionPence: number; status: string }[];
  /** Commission breakdown (from byRetailer). */
  commission: { txCount: number; commissionPence: number; gmvPence: number } | null;
}

async function loadDetail(
  ctx: PageContext,
  row: RetailerRow,
  signal: AbortSignal,
): Promise<RetailerDetail> {
  // No /api/admin/retailers/:id exists today — work from the list-row data we
  // already have. If/when the endpoint lands, swap to a real fetch.

  // Recent transactions per retailer aren't directly addressable either, so
  // try the optimistic per-retailer transactions endpoint and degrade.
  let recentTx: RetailerDetail["recentTx"] = [];
  try {
    type TxResp = { items: { id: string; createdAt: number; grossPence: number; commissionPence: number; status: string }[] };
    const txResp = await ctx.api<TxResp>(
      `/admin/retailers/${encodeURIComponent(row.id)}/transactions?limit=10`,
      { signal },
    );
    recentTx = txResp.items.slice(0, 10);
  } catch (err) {
    if (signal.aborted) throw err;
    // 404/501 → swallow; otherwise also swallow but log so console gives a hint.
    if (!(err instanceof ApiError && (err.status === 404 || err.status === 501))) {
      console.warn("[admin/retailers] per-retailer tx fetch failed:", err);
    }
  }

  // Commission summary — re-pull the platform overview for the byRetailer slice.
  let commission: RetailerDetail["commission"] = null;
  try {
    const ov = await ctx.api<AdminOverview>("/stripe/admin/overview", { signal });
    const match = ov.byRetailer.find((b) => b.retailerId === row.id);
    if (match) {
      commission = {
        txCount: match.txCount,
        commissionPence: match.commissionPence,
        gmvPence: match.gmvPence,
      };
    }
  } catch (err) {
    if (signal.aborted) throw err;
    // ignore — leave commission null.
  }

  return { retailer: row, recentTx, commission };
}

async function patchRetailerStatus(
  ctx: PageContext,
  id: string,
  status: "active" | "suspended",
  signal: AbortSignal,
): Promise<{ ok: boolean; stubbed: boolean }> {
  try {
    await ctx.api<{ ok: boolean }>(`/admin/retailers/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
      signal,
    });
    return { ok: true, stubbed: false };
  } catch (err) {
    if (signal.aborted) throw err;
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
      // Backend endpoint isn't there yet — pretend it worked so the demo flow
      // is still useful. Caller surfaces a "stubbed" toast.
      return { ok: true, stubbed: true };
    }
    throw err;
  }
}

// ── Filtering ────────────────────────────────────────────────────────────────

interface Filters {
  search: string;
  status: StatusFilter;
  tier: TierFilter;
}

function applyFilters(rows: RetailerRow[], f: Filters): RetailerRow[] {
  const q = f.search.trim().toLowerCase();
  return rows.filter((r) => {
    if (f.status !== "all" && r.status !== f.status) return false;
    if (f.tier !== "all" && r.tierN !== f.tier) return false;
    if (q) {
      const hay = [r.businessName, r.email, r.slug, r.contactName].join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

// ── Rendering ────────────────────────────────────────────────────────────────

function renderStatusPill(s: RetailerStatus): string {
  switch (s) {
    case "active":
      return `<span class="adm-pill adm-pill-ok">Active</span>`;
    case "suspended":
      return `<span class="adm-pill adm-pill-warn">Suspended</span>`;
    case "cancelled":
      return `<span class="adm-pill adm-pill-danger">Cancelled</span>`;
    case "onboarding":
      return `<span class="adm-pill adm-pill-info">Onboarding</span>`;
    case "pending":
      return `<span class="adm-pill adm-pill-info">Pending</span>`;
    default:
      return `<span class="adm-pill">—</span>`;
  }
}

function renderTable(host: HTMLElement, rows: RetailerRow[], selectedId: string | null): void {
  if (rows.length === 0) {
    host.innerHTML = `<div class="adm-empty">No retailers match your filters.</div>`;
    return;
  }
  host.innerHTML = `
    <div class="ret-table-wrap">
      <table class="adm-table ret-table">
        <thead>
          <tr>
            <th>Business</th>
            <th>Slug</th>
            <th>Email</th>
            <th>Tier</th>
            <th class="ret-num">Rolling turnover</th>
            <th class="ret-num">Rate</th>
            <th>Status</th>
            <th>Stripe</th>
            <th>Last login</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (r) => `
              <tr class="ret-row ${r.id === selectedId ? "ret-row--active" : ""}" data-id="${escapeHtml(r.id)}" tabindex="0">
                <td><b>${escapeHtml(r.businessName)}</b></td>
                <td class="ret-cell-muted"><code>${escapeHtml(r.slug)}</code></td>
                <td class="ret-cell-muted">${escapeHtml(r.email || "—")}</td>
                <td><span class="adm-pill adm-pill-info">T${r.tierN}</span></td>
                <td class="ret-num">${escapeHtml(gbpFromPence(r.rollingGmvPence))}</td>
                <td class="ret-num">${r.commissionRatePct.toFixed(1)}%</td>
                <td>${renderStatusPill(r.status)}</td>
                <td>${
                  r.stripeConnected
                    ? `<span class="adm-pill adm-pill-ok">Yes</span>`
                    : `<span class="adm-pill">No</span>`
                }</td>
                <td class="ret-cell-muted">${escapeHtml(relTime(r.lastLoginAt))}</td>
                <td class="ret-cell-muted">${escapeHtml(fmtDateShort(r.createdAt))}</td>
              </tr>
            `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderDetail(
  host: HTMLElement,
  detail: RetailerDetail | null,
  loading: boolean,
): void {
  if (loading) {
    host.innerHTML = `<div class="adm-loading">Loading retailer…</div>`;
    return;
  }
  if (!detail) {
    host.innerHTML = `
      <div class="ret-detail-empty">
        <div class="ret-detail-empty__icon" aria-hidden="true">🏪</div>
        <h3>Select a retailer</h3>
        <p>Click any row above to see their full profile, recent transactions, commissions, audit log and admin actions.</p>
      </div>
    `;
    return;
  }
  const r = detail.retailer;
  const addressStr = formatAddress(r.address);
  const commission = detail.commission;
  const isSuspended = r.status === "suspended";
  const suspendLabel = isSuspended ? "Reactivate" : "Suspend";
  const suspendClass = isSuspended ? "adm-btn" : "adm-btn adm-btn-danger";
  const isEnterprise = r.plan === "enterprise";
  const planName = isEnterprise ? "Enterprise" : "Standard";
  const planToggleLabel = isEnterprise ? "Switch to Standard" : "Switch to Enterprise";

  const txRows =
    detail.recentTx.length > 0
      ? detail.recentTx
          .map(
            (t) => `
            <tr>
              <td><code>${escapeHtml(t.id)}</code></td>
              <td class="ret-cell-muted">${escapeHtml(relTime(new Date(t.createdAt).toISOString()))}</td>
              <td class="ret-num"><b>${escapeHtml(gbpFromPence(t.grossPence))}</b></td>
              <td class="ret-num">${escapeHtml(gbpFromPence(t.commissionPence))}</td>
              <td>${
                t.status === "succeeded"
                  ? `<span class="adm-pill adm-pill-ok">${escapeHtml(t.status)}</span>`
                  : `<span class="adm-pill">${escapeHtml(t.status)}</span>`
              }</td>
            </tr>
          `,
          )
          .join("")
      : `<tr><td colspan="5" class="adm-empty" style="padding:1.2rem">No transactions yet for this retailer.</td></tr>`;

  host.innerHTML = `
    <article class="adm-card ret-detail" aria-label="Retailer drill-down">
      <header class="ret-detail__head">
        <div>
          <h2 class="ret-detail__title">${escapeHtml(r.businessName)} ${renderStatusPill(r.status)}</h2>
          <div class="ret-detail__meta">
            <span><b>Slug</b> <code>${escapeHtml(r.slug)}</code></span>
            <span><b>Email</b> ${escapeHtml(r.email || "—")}</span>
            <span><b>Phone</b> ${escapeHtml(r.phone || "—")}</span>
            <span><b>Contact</b> ${escapeHtml(r.contactName || "—")}</span>
          </div>
          <div class="ret-detail__address">${escapeHtml(addressStr)}</div>
        </div>
        <div class="ret-detail__actions">
          <button class="adm-btn adm-btn-ghost" type="button" data-action="plan-toggle" title="Move this retailer between the standard commission ladder and the enterprise ladder. Affects commission charged on future transactions only.">${planToggleLabel}</button>
          <button class="adm-btn adm-btn-ghost" type="button" data-action="impersonate">View As</button>
          <button class="adm-btn adm-btn-ghost" type="button" data-action="demo-reset" title="Clear this tenant's CRM to a clean slate for the next demo (keeps branding, settings &amp; catalogues)">Reset demo</button>
          <button class="${suspendClass}" type="button" data-action="suspend">${suspendLabel}</button>
          <button class="adm-btn adm-btn-danger" type="button" data-action="delete" title="Permanently remove this retailer. Billing &amp; Stripe history are preserved.">Delete Retailer</button>
        </div>
      </header>

      <div class="ret-detail__grid">
        <section class="ret-detail__cell">
          <h4 class="ret-detail__h4">Commission</h4>
          ${
            commission
              ? `
                <dl class="ret-detail__dl">
                  <div><dt>Commission plan</dt><dd>${planName}${isEnterprise ? ` <span class="adm-pill adm-pill-info">ENTERPRISE</span>` : ""}</dd></div>
                  <div><dt>Rolling turnover</dt><dd>${escapeHtml(gbpFromPence(commission.gmvPence))}</dd></div>
                  <div><dt>Commission earned</dt><dd>${escapeHtml(gbpFromPence(commission.commissionPence))}</dd></div>
                  <div><dt>Successful txns</dt><dd>${commission.txCount.toLocaleString("en-GB")}</dd></div>
                  <div><dt>Current tier</dt><dd>T${r.tierN} · ${r.commissionRatePct.toFixed(1)}%</dd></div>
                </dl>
              `
              : `<div class="adm-empty" style="padding:1rem"><div style="margin-bottom:.5rem"><b>Commission plan:</b> ${planName}${isEnterprise ? ` <span class="adm-pill adm-pill-info">ENTERPRISE</span>` : ""}</div>No commission data — retailer hasn't processed any payments through Stripe yet.</div>`
          }
        </section>

        <section class="ret-detail__cell">
          <h4 class="ret-detail__h4">Team members</h4>
          <div class="adm-empty" style="padding:1rem">
            User-list endpoint not wired up yet — team members will appear here.
          </div>
        </section>
      </div>

      <section class="ret-detail__cell">
        <h4 class="ret-detail__h4">Recent transactions</h4>
        <table class="adm-table ret-detail__table">
          <thead>
            <tr>
              <th>ID</th>
              <th>When</th>
              <th class="ret-num">Gross</th>
              <th class="ret-num">Commission</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>${txRows}</tbody>
        </table>
      </section>

      <section class="ret-detail__cell">
        <h4 class="ret-detail__h4">Audit log</h4>
        <div class="adm-empty" style="padding:1rem">
          Immutable audit log not implemented yet (inventory flags this as missing). Once the backend ships <code>/api/admin/audit/:retailerId</code>, every action against this retailer will surface here.
        </div>
      </section>
    </article>
  `;
}

function renderShell(container: HTMLElement): {
  bannerEl: HTMLElement;
  countEl: HTMLElement;
  searchEl: HTMLInputElement;
  statusEl: HTMLSelectElement;
  tierEl: HTMLSelectElement;
  tableEl: HTMLElement;
  detailEl: HTMLElement;
  refreshBtn: HTMLButtonElement;
  newBtn: HTMLButtonElement;
  confirmDialog: HTMLDialogElement;
  confirmTitleEl: HTMLElement;
  confirmBodyEl: HTMLElement;
  confirmOkBtn: HTMLButtonElement;
  confirmCancelBtn: HTMLButtonElement;
  createDialog: HTMLDialogElement;
  createForm: HTMLFormElement;
  createSubmitBtn: HTMLButtonElement;
  createCancelBtn: HTMLButtonElement;
  createErrorEl: HTMLElement;
} {
  container.innerHTML = `
    <section class="ret-root" aria-label="All retailers">
      <header class="ret-header">
        <div>
          <h1 class="ret-title">All Retailers <span class="ret-count" data-role="count">(—)</span></h1>
          <p class="ret-subtitle">Every retailer on the platform. Click a row to drill down.</p>
        </div>
        <div class="ret-header__actions">
          <button class="adm-btn" type="button" data-role="new">+ New retailer</button>
          <button class="adm-btn adm-btn-ghost" type="button" data-role="refresh">Refresh</button>
        </div>
      </header>

      <div class="ret-banner" data-role="banner" hidden></div>

      <div class="ret-filters">
        <input class="adm-input ret-search" type="search" placeholder="Search by name, email or slug…" data-role="search" />
        <select class="adm-select ret-filter-status" data-role="status">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select class="adm-select ret-filter-tier" data-role="tier">
          <option value="all">All tiers</option>
          <option value="1">Tier 1 · 3.3%</option>
          <option value="2">Tier 2 · 2.8%</option>
          <option value="3">Tier 3 · 2.3%</option>
          <option value="4">Tier 4 · 1.8%</option>
          <option value="5">Tier 5 · 1.5%</option>
        </select>
      </div>

      <div data-role="table"></div>

      <div data-role="detail"></div>

      <dialog class="ret-dialog" data-role="confirm">
        <form method="dialog" class="ret-dialog__form">
          <h3 class="ret-dialog__title" data-role="confirm-title">Confirm</h3>
          <p class="ret-dialog__body" data-role="confirm-body"></p>
          <div class="ret-dialog__actions">
            <button type="button" class="adm-btn adm-btn-ghost" data-role="confirm-cancel">Cancel</button>
            <button type="button" class="adm-btn adm-btn-danger" data-role="confirm-ok">Confirm</button>
          </div>
        </form>
      </dialog>

      <dialog class="ret-dialog ret-create" data-role="create">
        <form method="dialog" class="ret-dialog__form ret-create__form">
          <h3 class="ret-dialog__title">Create retailer</h3>
          <p class="ret-dialog__body">Creates the account and emails the retailer a set-password invite (valid 7 days). Commission tier and Stripe Connect are configured separately.</p>
          <label class="ret-field"><span>Business name <em>*</em></span><input class="adm-input" data-field="businessName" type="text" maxlength="200" autocomplete="off" required /></label>
          <label class="ret-field"><span>Email <em>*</em></span><input class="adm-input" data-field="email" type="email" maxlength="200" autocomplete="off" required /></label>
          <label class="ret-field"><span>Contact name</span><input class="adm-input" data-field="contactName" type="text" maxlength="120" autocomplete="off" /></label>
          <label class="ret-field"><span>Phone</span><input class="adm-input" data-field="phone" type="tel" maxlength="60" autocomplete="off" /></label>
          <label class="ret-field"><span>Address</span><input class="adm-input" data-field="address" type="text" maxlength="300" autocomplete="off" /></label>
          <label class="ret-field"><span>Status</span><select class="adm-select" data-field="status"><option value="active">Active</option><option value="suspended">Suspended</option></select></label>
          <div class="ret-create__error" data-role="create-error" hidden></div>
          <div class="ret-dialog__actions">
            <button type="button" class="adm-btn adm-btn-ghost" data-role="create-cancel">Cancel</button>
            <button type="button" class="adm-btn" data-role="create-submit">Create &amp; invite</button>
          </div>
        </form>
      </dialog>
    </section>
  `;

  return {
    bannerEl: container.querySelector<HTMLElement>("[data-role='banner']")!,
    countEl: container.querySelector<HTMLElement>("[data-role='count']")!,
    searchEl: container.querySelector<HTMLInputElement>("[data-role='search']")!,
    statusEl: container.querySelector<HTMLSelectElement>("[data-role='status']")!,
    tierEl: container.querySelector<HTMLSelectElement>("[data-role='tier']")!,
    tableEl: container.querySelector<HTMLElement>("[data-role='table']")!,
    detailEl: container.querySelector<HTMLElement>("[data-role='detail']")!,
    refreshBtn: container.querySelector<HTMLButtonElement>("[data-role='refresh']")!,
    newBtn: container.querySelector<HTMLButtonElement>("[data-role='new']")!,
    confirmDialog: container.querySelector<HTMLDialogElement>("[data-role='confirm']")!,
    confirmTitleEl: container.querySelector<HTMLElement>("[data-role='confirm-title']")!,
    confirmBodyEl: container.querySelector<HTMLElement>("[data-role='confirm-body']")!,
    confirmOkBtn: container.querySelector<HTMLButtonElement>("[data-role='confirm-ok']")!,
    confirmCancelBtn: container.querySelector<HTMLButtonElement>("[data-role='confirm-cancel']")!,
    createDialog: container.querySelector<HTMLDialogElement>("[data-role='create']")!,
    createForm: container.querySelector<HTMLFormElement>(".ret-create__form")!,
    createSubmitBtn: container.querySelector<HTMLButtonElement>("[data-role='create-submit']")!,
    createCancelBtn: container.querySelector<HTMLButtonElement>("[data-role='create-cancel']")!,
    createErrorEl: container.querySelector<HTMLElement>("[data-role='create-error']")!,
  };
}

// ── Page ─────────────────────────────────────────────────────────────────────

const retailersPage: Page = {
  name: "retailers",
  label: "Retailers",
  icon: "🏪",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const shell = renderShell(container);
    const {
      bannerEl,
      countEl,
      searchEl,
      statusEl,
      tierEl,
      tableEl,
      detailEl,
      refreshBtn,
      newBtn,
      confirmDialog,
      confirmTitleEl,
      confirmBodyEl,
      confirmOkBtn,
      confirmCancelBtn,
      createDialog,
      createForm,
      createSubmitBtn,
      createCancelBtn,
      createErrorEl,
    } = shell;

    let listController: AbortController | null = null;
    let detailController: AbortController | null = null;
    let mutateController: AbortController | null = null;
    let disposed = false;

    let allRows: RetailerRow[] = [];
    let filteredRows: RetailerRow[] = [];
    let selectedId: string | null = ctx.getCurrentRetailerId();
    let selectedDetail: RetailerDetail | null = null;
    const filters: Filters = { search: "", status: "all", tier: "all" };

    const setBanner = (msg: string | null, kind: "info" | "error" = "info"): void => {
      if (!msg) {
        bannerEl.hidden = true;
        bannerEl.textContent = "";
        bannerEl.classList.remove("ret-banner--error");
        return;
      }
      bannerEl.hidden = false;
      bannerEl.textContent = msg;
      bannerEl.classList.toggle("ret-banner--error", kind === "error");
    };

    const updateCount = (): void => {
      const totalShown = filteredRows.length;
      const totalAll = allRows.length;
      countEl.textContent =
        totalShown === totalAll
          ? `(${totalAll})`
          : `(${totalShown} of ${totalAll})`;
    };

    const applyAndRender = (): void => {
      filteredRows = applyFilters(allRows, filters);
      updateCount();
      renderTable(tableEl, filteredRows, selectedId);
    };

    const selectRetailer = async (id: string): Promise<void> => {
      const row = allRows.find((r) => r.id === id);
      if (!row) return;
      selectedId = id;
      ctx.setCurrentRetailerId(id);
      renderTable(tableEl, filteredRows, selectedId);
      renderDetail(detailEl, null, true);

      detailController?.abort();
      detailController = new AbortController();
      try {
        const detail = await loadDetail(ctx, row, detailController.signal);
        if (disposed || detailController.signal.aborted) return;
        selectedDetail = detail;
        renderDetail(detailEl, detail, false);
        // Scroll the drill-down into view.
        detailEl.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch (err) {
        if (detailController?.signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Couldn't load retailer (HTTP ${err.status})`
            : err instanceof Error
            ? err.message
            : "Couldn't load retailer";
        renderDetail(
          detailEl,
          { retailer: row, recentTx: [], commission: null },
          false,
        );
        ctx.showToast(msg, "error");
      }
    };

    const loadList = async (announce: boolean): Promise<void> => {
      if (disposed) return;
      listController?.abort();
      listController = new AbortController();
      const signal = listController.signal;
      refreshBtn.disabled = true;

      try {
        const { rows, overviewMissing } = await loadRetailers(ctx, signal);
        if (disposed || signal.aborted) return;
        allRows = rows;
        applyAndRender();
        if (overviewMissing) {
          setBanner(
            "Stripe overview not available — rolling turnover and tier columns will read as £0 / Tier 1 until it returns.",
            "info",
          );
        } else {
          setBanner(null);
        }

        // Re-hydrate detail if a retailer was selected.
        if (selectedId) {
          const stillThere = allRows.find((r) => r.id === selectedId);
          if (stillThere) {
            await selectRetailer(selectedId);
          } else {
            selectedId = null;
            ctx.setCurrentRetailerId(null);
            selectedDetail = null;
            renderDetail(detailEl, null, false);
          }
        } else {
          renderDetail(detailEl, null, false);
        }

        if (announce) ctx.showToast("Retailers refreshed", "success");
      } catch (err) {
        if (signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Couldn't load retailers (HTTP ${err.status})`
            : err instanceof Error
            ? err.message
            : "Couldn't load retailers";
        setBanner(msg, "error");
        if (announce) ctx.showToast(msg, "error");
      } finally {
        if (!disposed) refreshBtn.disabled = false;
      }
    };

    // ── Confirm dialog plumbing ────────────────────────────────────────────

    let confirmResolve: ((ok: boolean) => void) | null = null;

    const openConfirm = (title: string, body: string, okLabel: string, danger: boolean): Promise<boolean> => {
      confirmTitleEl.textContent = title;
      confirmBodyEl.textContent = body;
      confirmOkBtn.textContent = okLabel;
      confirmOkBtn.className = danger ? "adm-btn adm-btn-danger" : "adm-btn";
      try {
        confirmDialog.showModal();
      } catch {
        // showModal can throw if already open — close and reopen.
        confirmDialog.close();
        confirmDialog.showModal();
      }
      return new Promise<boolean>((resolve) => {
        confirmResolve = resolve;
      });
    };

    const onConfirmOk = (): void => {
      confirmDialog.close();
      confirmResolve?.(true);
      confirmResolve = null;
    };
    const onConfirmCancel = (): void => {
      confirmDialog.close();
      confirmResolve?.(false);
      confirmResolve = null;
    };
    confirmOkBtn.addEventListener("click", onConfirmOk);
    confirmCancelBtn.addEventListener("click", onConfirmCancel);
    // Native dialog "cancel" (Esc) — treat as cancel.
    const onDialogCancel = (): void => {
      confirmResolve?.(false);
      confirmResolve = null;
    };
    confirmDialog.addEventListener("cancel", onDialogCancel);

    // ── Action handlers ────────────────────────────────────────────────────

    const handleSuspend = async (): Promise<void> => {
      if (!selectedDetail) return;
      const r = selectedDetail.retailer;
      const goingTo: "active" | "suspended" =
        r.status === "suspended" ? "active" : "suspended";
      const verb = goingTo === "suspended" ? "Suspend" : "Reactivate";
      const ok = await openConfirm(
        `${verb} ${r.businessName}?`,
        goingTo === "suspended"
          ? "Suspending blocks their portal sign-in and pauses commission billing. You can reactivate at any time."
          : "Reactivating restores their portal sign-in and resumes billing.",
        verb,
        goingTo === "suspended",
      );
      if (!ok) return;

      mutateController?.abort();
      mutateController = new AbortController();
      try {
        const res = await patchRetailerStatus(ctx, r.id, goingTo, mutateController.signal);
        if (disposed) return;
        if (res.stubbed) {
          ctx.showToast(`${verb} stubbed — backend endpoint not wired yet`, "info");
          // Optimistically update local state so the UX still makes sense.
          r.status = goingTo;
          const idx = allRows.findIndex((x) => x.id === r.id);
          if (idx >= 0) allRows[idx] = { ...allRows[idx]!, status: goingTo };
        } else {
          ctx.showToast(`${r.businessName} ${goingTo === "suspended" ? "suspended" : "reactivated"}`, "success");
        }
        applyAndRender();
        renderDetail(detailEl, selectedDetail, false);
      } catch (err) {
        if (mutateController?.signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Failed to ${verb.toLowerCase()} retailer (HTTP ${err.status})`
            : err instanceof Error
            ? err.message
            : "Failed to change retailer status";
        ctx.showToast(msg, "error");
      }
    };

    const handleImpersonate = (): void => {
      if (!selectedDetail) return;
      // Master doc: "View As" — for now this stubs out with a toast. Implement
      // when /api/admin/impersonate is built.
      ctx.showToast(
        `View As stubbed — would open ${selectedDetail.retailer.businessName}'s retailer portal as ${ctx.user.email}.`,
        "info",
      );
    };

    const handleDemoReset = async (): Promise<void> => {
      if (!selectedDetail) return;
      const r = selectedDetail.retailer;
      const ok = await openConfirm(
        `Reset ${r.businessName} for demo?`,
        "This clears ALL customers and leads and cancels their pending SMS/email reminders, giving a clean slate for the next demo. Branding, settings, Stripe and supplier catalogues are kept. This cannot be undone.",
        "Reset demo",
        true,
      );
      if (!ok) return;

      mutateController?.abort();
      mutateController = new AbortController();
      try {
        const res = await ctx.api<{
          ok: boolean;
          customersCleared?: number;
          scheduledCommsCancelled?: number;
        }>(`/admin/retailers/${encodeURIComponent(r.id)}/demo-reset`, {
          method: "POST",
          signal: mutateController.signal,
        });
        if (disposed) return;
        const cleared = res.customersCleared ?? 0;
        const cancelled = res.scheduledCommsCancelled ?? 0;
        ctx.showToast(
          `${r.businessName} reset — ${cleared} customer${cleared === 1 ? "" : "s"} cleared, ${cancelled} scheduled message${cancelled === 1 ? "" : "s"} cancelled.`,
          "success",
        );
      } catch (err) {
        if (mutateController?.signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Demo reset failed (HTTP ${err.status})`
            : err instanceof Error
            ? err.message
            : "Demo reset failed";
        ctx.showToast(msg, "error");
      }
    };

    const handleDelete = async (): Promise<void> => {
      if (!selectedDetail) return;
      const r = selectedDetail.retailer;
      const ok = await openConfirm(
        `Delete ${r.businessName}?`,
        "This permanently removes the retailer from the platform and soft-deletes all their customers, leads, quotes and messages. Pending SMS/email reminders are cancelled and the local Stripe reference is removed. The Stripe Connect account, commission and payment history are preserved for audit. This cannot be undone.",
        "Delete Retailer",
        true,
      );
      if (!ok) return;

      mutateController?.abort();
      mutateController = new AbortController();
      try {
        const res = await ctx.api<{
          ok: boolean;
          customersSoftDeleted?: number;
        }>(`/admin/retailers/${encodeURIComponent(r.id)}/delete`, {
          method: "POST",
          signal: mutateController.signal,
        });
        if (disposed) return;
        if (!res.ok) {
          ctx.showToast("Delete failed", "error");
          return;
        }
        ctx.showToast(`${r.businessName} deleted`, "success");
        // Drop it from the detail panel and refresh the list (its file-store
        // record is gone, so it no longer appears).
        selectedId = null;
        selectedDetail = null;
        ctx.setCurrentRetailerId(null);
        renderDetail(detailEl, null, false);
        void loadList(false);
      } catch (err) {
        if (mutateController?.signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Delete failed (HTTP ${err.status})`
            : err instanceof Error
            ? err.message
            : "Delete failed";
        ctx.showToast(msg, "error");
      }
    };

    const handlePlanToggle = async (): Promise<void> => {
      if (!selectedDetail) return;
      const r = selectedDetail.retailer;
      const target: Plan = r.plan === "enterprise" ? "standard" : "enterprise";
      const ok = await openConfirm(
        `Move ${r.businessName} to the ${target === "enterprise" ? "Enterprise" : "Standard"} plan?`,
        target === "enterprise"
          ? "Future transactions will be charged on the Enterprise commission ladder (2.5% → 1.5%), keeping the same £500 per-transaction cap and rolling-12-month turnover logic. Past transactions are unaffected."
          : "Future transactions will revert to the Standard commission ladder (3.3% → 1.5%). Past transactions are unaffected.",
        target === "enterprise" ? "Switch to Enterprise" : "Switch to Standard",
        false,
      );
      if (!ok) return;

      mutateController?.abort();
      mutateController = new AbortController();
      try {
        await ctx.api<{ ok: boolean; plan: Plan }>(`/stripe/admin/retailer-plan`, {
          method: "POST",
          body: JSON.stringify({ retailerId: r.id, plan: target }),
          signal: mutateController.signal,
        });
        if (disposed) return;
        ctx.showToast(
          `${r.businessName} moved to the ${target === "enterprise" ? "Enterprise" : "Standard"} plan`,
          "success",
        );
        // Re-pull the overview so the plan map, row tier and detail all refresh.
        void loadList(false);
      } catch (err) {
        if (mutateController?.signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Plan change failed (HTTP ${err.status})`
            : err instanceof Error
            ? err.message
            : "Plan change failed";
        ctx.showToast(msg, "error");
      }
    };

    // ── Create-retailer dialog ─────────────────────────────────────────────

    const fieldVal = (f: string): string => {
      const el = createForm.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-field="${f}"]`);
      return (el?.value ?? "").trim();
    };
    const showCreateErr = (msg: string | null): void => {
      if (!msg) { createErrorEl.hidden = true; createErrorEl.textContent = ""; return; }
      createErrorEl.hidden = false;
      createErrorEl.textContent = msg;
    };
    const apiErrMsg = (err: unknown, fallback: string): string => {
      if (err instanceof ApiError) {
        try { const j = JSON.parse(err.message); if (j && typeof j.error === "string") return j.error; } catch { /* not JSON */ }
        return err.message || `HTTP ${err.status}`;
      }
      return err instanceof Error ? err.message : fallback;
    };

    const openCreate = (): void => {
      createForm.reset();
      showCreateErr(null);
      createSubmitBtn.disabled = false;
      try { createDialog.showModal(); } catch { createDialog.close(); createDialog.showModal(); }
      createForm.querySelector<HTMLInputElement>(`[data-field="businessName"]`)?.focus();
    };
    const closeCreate = (): void => { try { createDialog.close(); } catch { /* ignore */ } };

    const submitCreate = async (): Promise<void> => {
      const businessName = fieldVal("businessName");
      const email = fieldVal("email");
      if (!businessName) { showCreateErr("Business name is required."); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { showCreateErr("A valid email is required."); return; }
      showCreateErr(null);
      createSubmitBtn.disabled = true;
      mutateController?.abort();
      mutateController = new AbortController();
      try {
        const res = await ctx.api<{ ok: boolean; retailer?: { businessName: string; slug: string }; invited?: boolean }>(
          "/admin/retailers",
          {
            method: "POST",
            body: JSON.stringify({
              businessName,
              email,
              contactName: fieldVal("contactName"),
              phone: fieldVal("phone"),
              address: fieldVal("address"),
              status: fieldVal("status") || "active",
            }),
            signal: mutateController.signal,
          },
        );
        if (disposed) return;
        closeCreate();
        const name = res.retailer?.businessName ?? businessName;
        ctx.showToast(
          res.invited ? `${name} created — set-password invite emailed.` : `${name} created (invite email could not be sent).`,
          res.invited ? "success" : "info",
        );
        void loadList(false);
      } catch (err) {
        if (mutateController?.signal.aborted) return;
        showCreateErr(apiErrMsg(err, "Could not create retailer."));
      } finally {
        if (!disposed) createSubmitBtn.disabled = false;
      }
    };

    const onNew = (): void => openCreate();
    const onCreateCancel = (): void => closeCreate();
    const onCreateSubmit = (): void => { void submitCreate(); };
    const onCreateDialogCancel = (): void => closeCreate();
    newBtn.addEventListener("click", onNew);
    createCancelBtn.addEventListener("click", onCreateCancel);
    createSubmitBtn.addEventListener("click", onCreateSubmit);
    createDialog.addEventListener("cancel", onCreateDialogCancel);

    // ── Event wiring ───────────────────────────────────────────────────────

    const onSearch = (): void => {
      filters.search = searchEl.value;
      applyAndRender();
    };
    const onStatusChange = (): void => {
      filters.status = statusEl.value as StatusFilter;
      applyAndRender();
    };
    const onTierChange = (): void => {
      const v = tierEl.value;
      if (v === "all") {
        filters.tier = "all";
      } else {
        const n = Number(v);
        if (n === 1 || n === 2 || n === 3 || n === 4 || n === 5) {
          filters.tier = n;
        }
      }
      applyAndRender();
    };
    const onRefresh = (): void => {
      void loadList(true);
    };

    // Click delegation on the table — handles row clicks and detail-action clicks.
    const onContainerClick = (ev: MouseEvent): void => {
      const target = ev.target;
      if (!(target instanceof Element)) return;

      // Detail action buttons live inside detailEl.
      if (detailEl.contains(target)) {
        const btn = target.closest<HTMLButtonElement>("[data-action]");
        if (btn) {
          const action = btn.getAttribute("data-action");
          if (action === "suspend") void handleSuspend();
          else if (action === "impersonate") handleImpersonate();
          else if (action === "demo-reset") void handleDemoReset();
          else if (action === "delete") void handleDelete();
          else if (action === "plan-toggle") void handlePlanToggle();
          return;
        }
      }

      // Table row clicks.
      if (tableEl.contains(target)) {
        const row = target.closest<HTMLElement>("tr.ret-row");
        if (row?.dataset["id"]) {
          void selectRetailer(row.dataset["id"]);
        }
      }
    };

    // Keyboard accessibility for table rows.
    const onContainerKeydown = (ev: KeyboardEvent): void => {
      if (ev.key !== "Enter" && ev.key !== " ") return;
      const target = ev.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.matches("tr.ret-row") && target.dataset["id"]) {
        ev.preventDefault();
        void selectRetailer(target.dataset["id"]);
      }
    };

    searchEl.addEventListener("input", onSearch);
    statusEl.addEventListener("change", onStatusChange);
    tierEl.addEventListener("change", onTierChange);
    refreshBtn.addEventListener("click", onRefresh);
    container.addEventListener("click", onContainerClick);
    container.addEventListener("keydown", onContainerKeydown);

    // Initial render (empty drill-down placeholder).
    renderDetail(detailEl, null, false);
    renderTable(tableEl, [], null);

    void loadList(false);

    return (): void => {
      disposed = true;
      listController?.abort();
      detailController?.abort();
      mutateController?.abort();
      listController = null;
      detailController = null;
      mutateController = null;
      searchEl.removeEventListener("input", onSearch);
      statusEl.removeEventListener("change", onStatusChange);
      tierEl.removeEventListener("change", onTierChange);
      refreshBtn.removeEventListener("click", onRefresh);
      newBtn.removeEventListener("click", onNew);
      createCancelBtn.removeEventListener("click", onCreateCancel);
      createSubmitBtn.removeEventListener("click", onCreateSubmit);
      createDialog.removeEventListener("cancel", onCreateDialogCancel);
      container.removeEventListener("click", onContainerClick);
      container.removeEventListener("keydown", onContainerKeydown);
      confirmOkBtn.removeEventListener("click", onConfirmOk);
      confirmCancelBtn.removeEventListener("click", onConfirmCancel);
      confirmDialog.removeEventListener("cancel", onDialogCancel);
      try {
        confirmDialog.close();
        createDialog.close();
      } catch {
        /* ignore */
      }
    };
  },
};

registerPage(retailersPage);

export default retailersPage;
